import { combineEpics } from 'redux-observable';
import { of } from 'rxjs';

import { NEXT_CONFIG } from '@common/constants/config/nextConfig';
import { Sounds } from '@common/constants/config/sounds';
import { roundNumber } from '@common/methods/roundNumber/roundNumber';
import { config } from '@legacyApp/client/config';
import { game_config } from '@legacyApp/client/game_config';
import { updateStatsLocalStorage } from '@legacyApp/client/modules/app/gameSessionService';
import { capitalize } from '@legacyApp/client/modules/app/appService';
import audioService from '@legacyApp/client/modules/app/audioService';
import chartService from '@legacyApp/client/modules/app/chartService';
import { epic } from '@legacyApp/client/modules/app/epicService';
import { simpleTrans } from '@legacyApp/client/modules/translation/translationService';
import { APP_CLOSES } from '@legacyApp/client/store/app/app.constants';
import { OPTIONS } from '@legacyApp/client/store/options/options.constants';
import { scientificToDecimal } from '@legacyApp/methods/math/scientificToDecimal';
import { GameType } from '@modules/games/Game/constants/availableGames';
import {
	addLoseStreak,
	addProfit,
	addProfitAutoHistory,
	addProfitHistory,
	addWagered,
	addWinStreak,
	setAutoInitProfit,
	setTopBet,
	setTopBetAuto,
	setTopLoseStreak,
	setTopLoseStreakAuto,
	setTopLost,
	setTopLostAuto,
	setTopWin,
	setTopWinAuto,
	setTopWinStreak,
	setTopWinStreakAuto,
} from '@modules/games/Game/store/gameSession/gameSession.actions';
import {
	ADD_LOSE_STREAK,
	ADD_PROFIT,
	ADD_WIN_STREAK,
	BET_LOST,
	BET_WON,
	RESET_AUTO_SESSION,
} from '@modules/games/Game/store/gameSession/gameSession.constants';
import { BetState } from '@modules/games/Game/store/game/@types/PlaceBetResponse';

const betEpic = epic('betEpic', {
	actions: (ofType) => ofType(BET_LOST, BET_WON),
	callback: ({ action, store$ }) => {
		const result = [];
		// TODO: NEW GAME FLAG
		const game = action.payload.game.name;

		if (
			!action.payload.auto &&
			[GameType.DICE, GameType.LIMBO].some((name) => game === name) &&
			store$.value.options[OPTIONS.SOUND]
		) {
			setTimeout(
				() => {
					audioService.play(
						action.type === BET_LOST ? Sounds.lost : Sounds.won,
					);
				},
				game === GameType.LIMBO && store$.value.options.animation ? 350 : 300,
			);
		}

		const data = {
			game,
			auto: action.payload.auto,
		};

		result.push(
			addProfit({
				...data,
				value: parseFloat(action.payload.profit),
			}),
		);

		result.push(
			addWagered({
				...data,
				value: parseFloat(action.payload.amount),
			}),
		);

		return of(result);
	},
});

const profitHistoryEpic = epic('profitHistoryEpic', {
	actions: (ofType) => ofType(ADD_PROFIT),
	callback: ({ action, store$ }) => {
		const limit =
			game_config[action.payload.game] &&
			game_config[action.payload.game]?.chartLimit
				? game_config[action.payload.game]?.chartLimit
				: config.chartLimit;

		const data = chartService.updateProfitHistory(
			action.payload.value,
			store$.value.gameSession.profitHistory[action.payload.game]?.data,
			limit,
		);

		const totalData = chartService.updateProfitHistory(
			action.payload.value,
			store$.value.gameSession.profitHistory.total.data,
		);

		return of(
			addProfitHistory({
				...store$.value.gameSession.profitHistory,
				total: {
					data: totalData,
				},
				[action.payload.game]: {
					data,
				},
			}),
		);
	},
});

const profitHistoryAutoEpic = epic('profitHistoryAutoEpic', {
	actions: (ofType) => ofType(ADD_PROFIT),
	callback: ({ action, store$ }) => {
		if (!store$.value.gameAutobet.autoBetRolling[action.payload.game]) {
			return of();
		}

		const limit =
			game_config[action.payload.game] &&
			game_config[action.payload.game]?.chartLimit
				? game_config[action.payload.game]?.chartLimit
				: config.chartLimit;

		const data = chartService.updateProfitHistory(
			action.payload.value,
			store$.value.gameSession.profitHistoryAuto[action.payload.game]?.data,
			limit,
		);

		return of(
			addProfitAutoHistory({
				...store$.value.gameSession.profitHistoryAuto,
				[action.payload.game]: {
					data,
				},
			}),
		);
	},
});

const closesEpic = epic('closesEpic', {
	actions: (ofType) => ofType(APP_CLOSES),
	callback: ({ store$ }) => {
		updateStatsLocalStorage(store$.value.gameSession);

		return of();
	},
});

const streakEpic = epic('streakEpic', {
	actions: (ofType) => ofType(BET_LOST, BET_WON),
	callback: ({ action }) => {
		const data = {
			game: action.payload.game.name,
			auto: action.payload.auto,
			value: 1,
		};

		return action.type === BET_WON
			? of(addWinStreak(data))
			: of(addLoseStreak(data));
	},
});

const topStreakEpic = epic('topStreakEpic', {
	actions: (ofType) => ofType(ADD_WIN_STREAK, ADD_LOSE_STREAK),
	callback: ({ action, store$ }) => {
		const isWin = action.type === ADD_WIN_STREAK;

		const label = isWin ? BetState.WIN : BetState.LOSE;

		const currStreak =
			store$.value.gameSession[`${label}Streak`][action.payload.game];

		const currStreakAuto =
			store$.value.gameSession[`${label}StreakAuto`][action.payload.game];

		const data = {
			game: action.payload.game,
			auto: action.payload.auto,
		};

		const result = [];

		if (
			currStreak >
			store$.value.gameSession[`top${capitalize(label)}Streak`][
				action.payload.game
			]
		) {
			const func = isWin ? setTopWinStreak : setTopLoseStreak;

			result.push(
				func({
					...data,
					value: currStreak,
				}),
			);
		}

		if (
			currStreakAuto >
			store$.value.gameSession[`top${capitalize(label)}StreakAuto`][
				action.payload.game
			]
		) {
			const funcAuto = isWin ? setTopWinStreakAuto : setTopLoseStreakAuto;

			result.push(
				funcAuto({
					...data,
					value: currStreakAuto,
				}),
			);
		}

		return of(result);
	},
});

const topProfitEpic = epic('topProfitEpic', {
	actions: (ofType) => ofType(BET_LOST, BET_WON),
	callback: ({ action, store$ }) => {
		const isWin = action.type === BET_WON;

		const game = action.payload.game.name;

		const currProfit = parseFloat(action.payload.profit.replace('-', ''));

		const data = {
			game,
			auto: action.payload.auto,
			value: currProfit,
		};

		const result = [];

		if (
			currProfit >
			store$.value.gameSession[`top${isWin ? 'Win' : 'Lost'}`][game]
		) {
			const func = isWin ? setTopWin : setTopLost;

			result.push(func(data));
		}

		if (
			currProfit >
				store$.value.gameSession[`top${isWin ? 'Win' : 'Lost'}Auto`][game] &&
			action.payload.auto
		) {
			const funcAuto = isWin ? setTopWinAuto : setTopLostAuto;

			result.push(funcAuto(data));
		}

		return of(result);
	},
});

const topBetEpic = epic('topBetEpic', {
	actions: (ofType) => ofType(BET_LOST, BET_WON),
	callback: ({ action, store$ }) => {
		const currBet = parseFloat(action.payload.amount);

		const game = action.payload.game.name;

		const data = {
			game,
			auto: action.payload.auto,
			value: currBet,
		};

		const result = [];

		if (currBet > store$.value.gameSession.topBet[game]) {
			result.push(setTopBet(data));
		}

		if (
			currBet > store$.value.gameSession.topBetAuto[game] &&
			action.payload.auto
		) {
			result.push(setTopBetAuto(data));
		}

		return of(result);
	},
});

const autobetInitProfitEpic = epic('autobetInitProfitEpic', {
	actions: (ofType) => ofType(RESET_AUTO_SESSION),
	callback: ({ store$ }) => {
		return of(setAutoInitProfit(store$.value.gameSession.profit));
	},
});

const profitInTabNameEpic = epic('profitInTabNameEpic', {
	actions: (ofType) => ofType(ADD_PROFIT),
	callback: ({ store$, action }) => {
		if (!NEXT_CONFIG.DEV) {
			return of();
		}

		if (
			!store$.value.app.visible &&
			store$.value.gameAutobet.autoBetRolling[action.payload.game] &&
			store$.value.game.activeGame === action.payload.game
		) {
			if (process.browser) {
				document.title =
					simpleTrans('{{amount}} {{currency}} profit - {{appName}}', {
						amount: scientificToDecimal(
							roundNumber(
								store$.value.gameSession.profit[action.payload.game] -
									store$.value.gameSession.initAutoProfit[action.payload.game],
								config.decimals.balance,
							),
						),
						currency: store$.value.user.activeCurrency.toUpperCase(),
						appName: config.appName,
					}) || '';
			}
		}

		return of();
	},
});

const gameSessionEpic = combineEpics(
	profitHistoryEpic,
	betEpic,
	closesEpic,
	streakEpic,
	topStreakEpic,
	topProfitEpic,
	topBetEpic,
	autobetInitProfitEpic,
	profitHistoryAutoEpic,
	profitInTabNameEpic,
);

export default gameSessionEpic;
